import type { ReactNode } from 'react';
import { ApiError } from '../../api/client.js';
import type { AuditClock, Envelope } from '../../api/types.js';
import { AnalystButton } from '../Analyst/AnalystButton';
import { EmptyState, ErrorBanner } from '../States/States.js';
import { Header } from '../../shell/Header.js';
import type { QueryResult } from '../../store/StoreProvider.js';
import { color, fontSize, radius, space } from '../../theme/tokens.js';

/**
 * The frame and the states every audit-data page shares — the Audit page (spec 007) and Alerts
 * (spec 008, R-14).
 *
 * ⚠ Shared, not copied: the two pages show the SAME audit data under the same constitution
 * conditions, and two copies of "when is it stale" or "what does a kept copy look like" is how they
 * would come to disagree about one run. Three rules live here:
 *
 * - **The header shows the AUDIT's clock, never the snapshot's as-of** (FR-AU28, FR-AL05).
 * - **The staleness word waits for a fresh check** (AU-19, FR-AL26): it shows only when the server
 *   still says `stale` and nothing is in flight, so an old browser copy is never reported stale.
 * - **A kept copy survives an outage** (FR-AU37, FR-AL20): the copy stays on screen with when it was
 *   fetched; with nothing held, the error banner and Retry. Never a blank or zero-filled table.
 */

function fetchedAtTime(ms: number | null): string {
  if (!ms) return 'earlier';
  return new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function codeOf(error: Error | null): string | null {
  return error instanceof ApiError ? error.code : null;
}

/**
 * The body for a query: loading, a refusal state, the error banner, or `render(data)`. Loading is
 * the state's own `skeleton` — the shape of what is coming, not one generic block.
 */
export function AuditBody<T>({
  state,
  render,
  skeleton,
  pageName = 'The Audit page',
  plural = false,
}: {
  state: QueryResult<Envelope<T>>;
  render: (data: T, envelope: Envelope<T>) => ReactNode;
  skeleton: ReactNode;
  /** How the refusal states name the page: "The Audit page is switched off." */
  pageName?: string;
  /** "Alerts ARE switched off." */
  plural?: boolean;
}) {
  const code = codeOf(state.error);
  const held = state.data?.data ?? null;

  if (held !== null) {
    return (
      <>
        {state.error && code?.startsWith('AUDIT_UPSTREAM') ? (
          <div
            role="status"
            style={{
              background: color.noticeBg,
              color: color.noticeInk,
              borderRadius: radius['3xl'],
              padding: '10px 16px',
              marginBottom: space['4xl'],
              fontSize: fontSize.captionStrong,
            }}
          >
            Couldn’t reach the audit service — showing results fetched {fetchedAtTime(state.fetchedAt)}.
          </div>
        ) : null}
        {render(held, state.data!)}
      </>
    );
  }
  if (state.loading) return <>{skeleton}</>;
  if (code === 'AUDIT_DISABLED') {
    return (
      <EmptyState
        title={`${pageName} ${plural ? 'are' : 'is'} switched off.`}
        detail="Ask an administrator if you expected to see it."
      />
    );
  }
  if (code === 'AUDIT_NO_ROSTER') {
    return (
      <EmptyState
        title="No roster yet"
        detail={`${pageName} lists recruiters from the daily roster, which has not run yet.`}
      />
    );
  }
  if (code === 'AUDIT_UNKNOWN' || code === 'INVALID_PARAMS' || code === 'NOT_FOUND') {
    return <EmptyState title="Unknown audit" detail="There is no audit at this address, or the date is not valid." />;
  }
  return <ErrorBanner message={state.error?.message} onRetry={state.reload} />;
}

/** Header (with the audit's clock) and the page's padded body. */
export function AuditFrame({
  crumb,
  title,
  envelope,
  clock,
  revalidating,
  pending,
  children,
}: {
  crumb: string;
  title: string;
  envelope: Pick<Envelope<unknown>, 'asOf' | 'syncedAt' | 'syncStatus' | 'staleness'> | null | undefined;
  clock: AuditClock | null;
  revalidating: boolean;
  /** Nothing has answered yet: no "No data yet" in the header. */
  pending: boolean;
  children: ReactNode;
}) {
  return (
    <>
      <Header
        crumb={crumb}
        title={title}
        asOf={envelope?.asOf ?? null}
        syncedAt={envelope?.syncedAt ?? null}
        syncStatus={envelope?.syncStatus ?? 'no_data'}
        staleness={envelope?.staleness ?? null}
        actions={<AnalystButton />}
        clock={{
          label: 'Audit run',
          date: clock?.latestRunDate ?? null,
          completedAt: clock?.completedAt ?? null,
          stale: (clock?.stale ?? false) && !revalidating,
          pending,
        }}
      />
      <div style={{ padding: `${space['6xl']} ${space['8xl']}` }}>{children}</div>
    </>
  );
}
